import { useEffect, useRef } from 'react';
import { logInfo } from '../utils/remoteLogger';

interface WakeLockSentinel extends EventTarget {
  release(): Promise<void>;
}

interface WakeLock {
  request(type: 'screen'): Promise<WakeLockSentinel>;
}

interface NavigatorWithWakeLock extends Navigator {
  wakeLock: WakeLock;
}

const TAG = 'useWakeLock';

const useWakeLock = (active: boolean) => {
  const wakeLockRef = useRef<WakeLockSentinel | null>(null);
  const unsupportedRef = useRef(false);
  const pendingRef = useRef<Promise<void> | null>(null);

  useEffect(() => {
    if (!active || !('wakeLock' in navigator)) return;
    const { wakeLock } = navigator as NavigatorWithWakeLock;

    let released = false;

    function handleRelease() {
      logInfo(TAG, 'WakeLock inactive');
      wakeLockRef.current = null;
      if (!released && document.visibilityState === 'visible') {
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        requestWakeLock();
      }
    }

    function requestWakeLock() {
      if (pendingRef.current || wakeLockRef.current || unsupportedRef.current)
        return;
      const pending = wakeLock
        .request('screen')
        .then((sentinel) => {
          if (released) {
            sentinel.release().catch(() => {});
            return;
          }
          logInfo(TAG, 'WakeLock active');
          wakeLockRef.current = sentinel;
          sentinel.addEventListener('release', handleRelease);
        })
        .catch((err) => {
          if (err instanceof Error && err.name === 'NotAllowedError') {
            unsupportedRef.current = true;
            logInfo(TAG, `WakeLock not supported on this platform`);
          } else {
            logInfo(
              TAG,
              `WakeLock request failed with error ${
                err instanceof Error ? err.name : 'unknown'
              }, message: ${err instanceof Error ? err.message : String(err)}`
            );
          }
        })
        .finally(() => {
          pendingRef.current = null;
        });
      pendingRef.current = pending;
    }

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !released) {
        requestWakeLock();
      }
    };

    requestWakeLock();
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      released = true;
      document.removeEventListener('visibilitychange', onVisibilityChange);
      const sentinel = wakeLockRef.current;
      if (sentinel) {
        sentinel.release().catch(() => {});
      }
      wakeLockRef.current = null;
    };
  }, [active]);
};

export default useWakeLock;
